import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { Account } from '@/types'
import { ImageIcon, Loader2, CheckCircle, XCircle, X } from 'lucide-react'

type FolderImage = { id: string; url: string; mediaType?: string }
type Folder = { id: string; name: string; images: FolderImage[] }
type ResultRow = { name: string; pageId?: string; status: 'ok' | 'fail'; error?: string | null }

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  tags: string[]
  accounts: Account[]
}

export function BulkPhotoModal({ open, onOpenChange, tags, accounts }: Props) {
  const [folders, setFolders] = useState<Folder[]>([])
  const [loadingFolders, setLoadingFolders] = useState(false)
  const [tag, setTag] = useState('')
  const [folderId, setFolderId] = useState('')
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)
  const [results, setResults] = useState<ResultRow[]>([])
  const [summary, setSummary] = useState<{ total: number; ok: number; fail: number } | null>(null)

  // Carrega as pastas da Biblioteca ao abrir
  useEffect(() => {
    if (!open) return
    setLoadingFolders(true)
    const token = localStorage.getItem('auth_token')
    fetch('/api/v2/media-folders', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((data: Folder[]) => setFolders(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Erro ao carregar as pastas da Biblioteca'))
      .finally(() => setLoadingFolders(false))
  }, [open])

  // Reseta o estado quando fecha
  useEffect(() => {
    if (open) return
    setTag(''); setFolderId(''); setRunning(false)
    setProgress(null); setResults([]); setSummary(null)
  }, [open])

  const pageCount = useMemo(
    () => accounts.filter(a => (a.tags || []).includes(tag)).length,
    [accounts, tag]
  )
  const selectedFolder = useMemo(() => folders.find(f => f.id === folderId), [folders, folderId])
  const imageCount = useMemo(
    () => (selectedFolder?.images || []).filter(i => (i.mediaType || 'image') === 'image').length,
    [selectedFolder]
  )
  const canRun = !!tag && !!folderId && imageCount > 0 && pageCount > 0 && !running

  async function run() {
    setRunning(true)
    setResults([]); setSummary(null); setProgress(null)
    try {
      const token = localStorage.getItem('auth_token')
      const resp = await fetch('/api/v2/accounts/bulk-photo-by-tag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tag, folderId }),
      })
      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => null)
        toast.error(err?.error || 'Erro ao iniciar a troca de fotos')
        setRunning(false)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'start') setProgress({ current: 0, total: data.total })
              else if (eventType === 'progress') {
                setProgress({ current: data.current, total: data.total })
                setResults(prev => [...prev, data.account])
              } else if (eventType === 'done') {
                setSummary({ total: data.total, ok: data.ok, fail: data.fail })
                // avisa os avatares do painel pra recarregar (fura cache)
                window.dispatchEvent(new Event('page-photos-updated'))
              }
            } catch { /* ignore */ }
            eventType = ''
          }
        }
      }
    } catch {
      toast.error('Erro durante a troca de fotos')
    } finally {
      setRunning(false)
    }
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <div className="flex items-center gap-2 mb-1">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Fotos de perfil em massa</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Escolha uma tag e uma pasta da Biblioteca. Cada página da tag recebe uma foto aleatória da pasta.
        </p>

        {!running && !summary && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tag</label>
              <select
                value={tag}
                onChange={e => setTag(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">Selecione a tag…</option>
                {tags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Pasta da Biblioteca</label>
              <select
                value={folderId}
                onChange={e => setFolderId(e.target.value)}
                disabled={loadingFolders}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">{loadingFolders ? 'Carregando…' : 'Selecione a pasta…'}</option>
                {folders.map(f => {
                  const n = (f.images || []).filter(i => (i.mediaType || 'image') === 'image').length
                  return <option key={f.id} value={f.id}>{f.name} ({n} imagens)</option>
                })}
              </select>
            </div>

            {tag && folderId && (
              <div className="rounded-md bg-secondary/50 border border-border p-3 text-sm">
                <b>{pageCount}</b> página(s) da tag <b>{tag}</b> vão receber fotos aleatórias
                da pasta <b>{selectedFolder?.name}</b> ({imageCount} imagens).
                {pageCount === 0 && <div className="text-amber-500 mt-1">Nenhuma página nessa tag.</div>}
                {imageCount === 0 && <div className="text-amber-500 mt-1">A pasta não tem imagens.</div>}
                <div className="text-xs text-muted-foreground mt-1">
                  Páginas com token emprestado/inválido são puladas (aparecem como falha).
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <DialogClose asChild><Button variant="outline" size="sm">Cancelar</Button></DialogClose>
              <Button size="sm" disabled={!canRun} onClick={run}>Aplicar fotos</Button>
            </div>
          </div>
        )}

        {(running || summary) && (
          <div className="space-y-3">
            <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all duration-200" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-sm text-muted-foreground">
              {progress ? `${progress.current} / ${progress.total}` : '…'}
              {summary && <span className="ml-2 text-foreground">— {summary.ok} ok, {summary.fail} falha(s)</span>}
            </div>

            <div className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border/50">
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  {r.status === 'ok'
                    ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                  <span className="truncate flex-1">{r.name}</span>
                  {r.status === 'fail' && r.error && (
                    <span className="text-[11px] text-muted-foreground truncate max-w-[180px]" title={r.error}>{r.error}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              {summary && !running && (
                <Button variant="outline" size="sm" onClick={() => { setSummary(null); setResults([]); setProgress(null) }}>
                  Nova rodada
                </Button>
              )}
              <DialogClose asChild>
                <Button size="sm" disabled={running}>{running ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Aplicando…</> : 'Fechar'}</Button>
              </DialogClose>
            </div>
          </div>
        )}

        <DialogClose asChild>
          <button className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}
